import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Loader2,
    Reply,
    Forward,
    PenSquare,
    Paperclip,
    Send,
    X,
} from "lucide-react";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { sendEmail } from "../../lib/api";
import { listAllFolders, listFiles as apiListFiles } from "../../lib/api/files";
import { api } from "../../lib/api/_client";
import { fmtSize, quoteOriginal } from "./utils";
import Row from "./Row";

export default function ComposeDialog({ state, onClose, account, accounts = [], isAllInboxes = false }) {
    const [to, setTo] = useState("");
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [includeSig, setIncludeSig] = useState(true);
    const [showFilePicker, setShowFilePicker] = useState(false);
    const fileInputRef = useRef(null);

    // When "All inboxes" is the active context, the user MUST pick a
    // From account before sending. For reply/forward we default to the
    // account the source message came from.
    const sendableAccounts = accounts.filter((a) => a.id !== "__all__");
    const sourceAcctId = state?.source?._account_id;
    const initialFromId =
        sourceAcctId
        || (isAllInboxes ? (sendableAccounts[0]?.id || "") : (account?.id || ""));
    const [fromAccountId, setFromAccountId] = useState(initialFromId);

    const fromAccount = isAllInboxes
        ? sendableAccounts.find((a) => a.id === fromAccountId) || sendableAccounts[0] || null
        : account;

    const open = !!state && !!(isAllInboxes ? sendableAccounts.length > 0 : account);
    const source = state?.source || null;
    const mode = state?.mode || "compose";

    // Build the seed content from reply/forward/compose mode.
    useEffect(() => {
        if (!open) return;
        // Re-snap from account when dialog opens for a new message.
        setFromAccountId(initialFromId);
        if (mode === "reply" && source) {
            setTo(source.from_addr || "");
            setCc("");
            setBcc("");
            setSubject(source.subject?.startsWith("Re:") ? source.subject : `Re: ${source.subject || ""}`);
            setBody(quoteOriginal(source));
        } else if (mode === "forward" && source) {
            setTo(""); setCc(""); setBcc("");
            setSubject(source.subject?.startsWith("Fwd:") ? source.subject : `Fwd: ${source.subject || ""}`);
            setBody(quoteOriginal(source, "Forwarded message"));
        } else {
            setTo(""); setCc(""); setBcc(""); setSubject(""); setBody("");
        }
        setFiles([]);
        setIncludeSig(true);
        /* eslint-disable-next-line */
    }, [open, mode, source]);

    if (!open) return null;

    const composeBody = () => {
        let out = body;
        if (includeSig && fromAccount?.signature_text) {
            out = `${out}\n\n--\n${fromAccount.signature_text}`;
        }
        return out;
    };

    const composeHtml = () => {
        if (!includeSig || !fromAccount?.signature_image_url) return "";
        const safeBody = (body || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>");
        const sigText = (fromAccount.signature_text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>");
        return `<div>${safeBody}<br><br>--<br><div style="margin-top:8px;">${sigText}<br><img src="${fromAccount.signature_image_url}" style="max-height:80px;margin-top:6px;" alt="signature"></div></div>`;
    };

    const send = async () => {
        if (!fromAccount?.id) { toast.error("Pick a From account first"); return; }
        const toList = to.split(",").map((s) => s.trim()).filter(Boolean);
        const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
        const bccList = bcc.split(",").map((s) => s.trim()).filter(Boolean);
        if (toList.length === 0) { toast.error("At least one recipient required"); return; }
        setSending(true);
        try {
            await sendEmail(fromAccount.id, {
                to: toList, cc: ccList, bcc: bccList,
                subject,
                body_text: composeBody(),
                body_html: composeHtml(),
                in_reply_to: source?.message_id || "",
                references: source?.references || [],
                reply_to_uid: source?.uid || undefined,
                reply_to_folder: source?.folder || undefined,
            }, files);
            toast.success("Sent");
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const totalFileSize = files.reduce((acc, f) => acc + f.size, 0);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                className="bg-[#0A1128] border border-[#2A3655] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="email-compose-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white">
                        {mode === "reply" ? <Reply className="w-5 h-5 text-[#00A3FF]" /> : mode === "forward" ? <Forward className="w-5 h-5 text-[#00A3FF]" /> : <PenSquare className="w-5 h-5 text-[#00A3FF]" />}
                        {mode === "reply" ? "Reply" : mode === "forward" ? "Forward" : "New message"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                    {/* From picker — visible when there are 2+ accounts so the
                        user can choose which mailbox to send from. Required
                        when activated from the "All inboxes" virtual view. */}
                    {sendableAccounts.length > 1 && (
                        <div className="flex items-center gap-3">
                            <span className="text-xs uppercase tracking-wider text-slate-400 font-bold w-16 shrink-0">From</span>
                            <select
                                value={fromAccountId || ""}
                                onChange={(e) => setFromAccountId(e.target.value)}
                                data-testid="email-compose-from"
                                className="flex-1 bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00A3FF]"
                            >
                                {sendableAccounts.map((a) => {
                                    const tag = a.auth_type === "oauth"
                                        ? (a.provider === "microsoft" ? "MS · " : "OAuth · ")
                                        : "";
                                    return (
                                        <option key={a.id} value={a.id}>
                                            {tag}{a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}
                    <Row label="To" value={to} onChange={setTo} testId="email-compose-to" placeholder="customer@example.com, …" />
                    <Row label="Cc" value={cc} onChange={setCc} testId="email-compose-cc" placeholder="optional" />
                    <Row label="Bcc" value={bcc} onChange={setBcc} testId="email-compose-bcc" placeholder="optional" />
                    <Row label="Subject" value={subject} onChange={setSubject} testId="email-compose-subject" />
                    <textarea
                        rows={10}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        data-testid="email-compose-body"
                        placeholder="Your message…"
                        className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF] resize-none"
                    />
                    <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                        <label className="inline-flex items-center gap-1.5 text-slate-300">
                            <input
                                type="checkbox"
                                checked={includeSig}
                                onChange={(e) => setIncludeSig(e.target.checked)}
                                disabled={!fromAccount?.signature_text && !fromAccount?.signature_image_url}
                                data-testid="email-compose-include-signature"
                                className="accent-[#00A3FF]"
                            />
                            Include signature
                            {(!fromAccount?.signature_text && !fromAccount?.signature_image_url) && (
                                <span className="text-slate-500 italic">(set one in Settings)</span>
                            )}
                        </label>
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] text-slate-300 hover:text-[#00A3FF] text-xs font-bold uppercase tracking-wider"
                            data-testid="email-compose-attach"
                        >
                            <Paperclip className="w-3 h-3" /> Attach
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowFilePicker(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] text-slate-300 hover:text-[#00A3FF] text-xs font-bold uppercase tracking-wider"
                            data-testid="email-compose-attach-from-files"
                        >
                            <Paperclip className="w-3 h-3" /> From Files
                        </button>
                        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => {
                            const list = Array.from(e.target.files || []);
                            e.target.value = "";
                            setFiles((prev) => [...prev, ...list]);
                        }} />
                    </div>
                    {files.length > 0 && (
                        <div className="space-y-1 pt-1">
                            {files.map((f, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs text-slate-300 bg-[#1C253C]/40 rounded-sm px-2 py-1">
                                    <Paperclip className="w-3 h-3 text-[#00A3FF]" />
                                    <span className="flex-1 truncate">{f.name}</span>
                                    <span className="text-slate-500">{fmtSize(f.size)}</span>
                                    <button
                                        onClick={() => setFiles(files.filter((_, j) => j !== i))}
                                        className="text-slate-500 hover:text-red-400"
                                    ><X className="w-3 h-3" /></button>
                                </div>
                            ))}
                            <p className="text-[10px] text-slate-500">Total: {fmtSize(totalFileSize)}</p>
                        </div>
                    )}
                    {showFilePicker && (
                        <FilePickerDialog
                            onClose={() => setShowFilePicker(false)}
                            onPick={async (fileIds) => {
                                setShowFilePicker(false);
                                if (!fileIds.length) return;
                                try {
                                    const res = await api.post("/files/attachments", { file_ids: fileIds });
                                    const attached = (res.data.attachments || []).map((a) => {
                                        const bytes = Uint8Array.from(atob(a.base64), (c) => c.charCodeAt(0));
                                        return new File([bytes], a.name, { type: a.mime_type });
                                    });
                                    setFiles((prev) => [...prev, ...attached]);
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Attach failed.");
                                }
                            }}
                        />
                    )}
                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#2A3655]">
                        <button type="button" onClick={onClose} disabled={sending}
                            className="px-4 py-2 rounded-sm border border-[#2A3655] text-slate-300 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                            Cancel
                        </button>
                        <Button type="button" onClick={send} disabled={sending}
                            data-testid="email-compose-send"
                            className="bg-[#00A3FF] hover:bg-[#3395FF] text-white font-bold h-9 px-5 disabled:opacity-50">
                            {sending ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5 mr-2" /> Send</>}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


// iter-133: quick file-picker for attaching files stored in the app's
// Files manager. Renders a flat searchable list per current folder so
// users don't have to leave the compose dialog.
function FilePickerDialog({ onClose, onPick }) {
    const [parent, setParent] = useState("root");
    const [breadcrumb, setBreadcrumb] = useState([{ file_id: "root", name: "Home" }]);
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const d = await apiListFiles(parent);
                setItems(d.items || []);
                setBreadcrumb(d.breadcrumb || [{ file_id: "root", name: "Home" }]);
            } finally {
                setLoading(false);
            }
        })();
    }, [parent]);

    const toggle = (id) => {
        setSelected((prev) => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={onClose}
            data-testid="email-file-picker"
        >
            <div
                className="bg-[#131A2F] border border-[#2A3655] rounded max-w-2xl w-full max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-3 border-b border-[#2A3655] flex items-center gap-2 flex-wrap">
                    {breadcrumb.map((c, i) => (
                        <React.Fragment key={c.file_id}>
                            {i > 0 && <span className="text-slate-500">/</span>}
                            <button
                                onClick={() => setParent(c.file_id)}
                                className="text-sm text-slate-200 hover:text-[#00A3FF]"
                            >
                                {c.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-6 text-center text-slate-500 text-sm">Empty folder.</div>
                    ) : (
                        <div className="divide-y divide-[#2A3655]/60">
                            {items.map((it) => (
                                <div
                                    key={it.file_id}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-[#1C253C]/60 text-sm text-slate-200"
                                    data-testid={`email-file-picker-row-${it.file_id}`}
                                >
                                    {it.is_folder ? (
                                        <button onClick={() => setParent(it.file_id)} className="flex-1 text-left">
                                            📁 {it.name}
                                        </button>
                                    ) : (
                                        <label className="flex-1 flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(it.file_id)}
                                                onChange={() => toggle(it.file_id)}
                                            />
                                            📄 <span className="truncate">{it.name}</span>
                                            <span className="text-xs text-slate-500 ml-auto">{fmtSize(it.size)}</span>
                                        </label>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="p-3 border-t border-[#2A3655] flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-1.5 rounded-sm border border-[#2A3655] text-slate-300 text-xs font-bold uppercase tracking-wider">
                        Cancel
                    </button>
                    <button
                        onClick={() => onPick(Array.from(selected))}
                        disabled={selected.size === 0}
                        data-testid="email-file-picker-attach"
                        className="px-3 py-1.5 rounded-sm bg-[#007AFF] hover:bg-[#0369A1] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40"
                    >
                        Attach {selected.size ? `(${selected.size})` : ""}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Keep import list happy even if listAllFolders isn't used in-file yet.
export const __attach_helpers = { listAllFolders };
